import { SelfContainedCommand, CommandExecutionContext } from '../../types/commands';
import { StandardToolResult, createSuccessResult, createErrorResult } from '../../types/standardToolResults';
import { createFileLogger } from '../../utils/logger';

const log = createFileLogger('OPEN_DIALOG_CREATE');

export const openDialogCreateCommand: SelfContainedCommand = {
  id: 'open_dialog_create',
  description: '🔍 CREATE: Create an OpenDialog entity for entity picking',
  autonomyLevel: 'auto',
  visualTreatment: 'silent',
  
  schema: {
    parameters: [
      {
        name: 'title',
        type: 'string',
        description: 'Title for the dialog',
        required: false,
        defaultValue: 'Open'
      },
      {
        name: 'spaceId',
        type: 'string',
        description: 'Space ID',
        required: false
      },
      {
        name: 'currentFolderId',
        type: 'string',
        description: 'Current folder to start browsing from',
        required: false
      },
      {
        name: 'windowId',
        type: 'string',
        description: 'Window ID to replace (optional)',
        required: false
      },
      {
        name: 'createWindow',
        type: 'boolean',
        description: 'Whether to create a new window',
        required: false,
        defaultValue: true
      }
    ]
  },
  
  examples: {
    good: [
      {
        input: { title: 'Open' },
        expectedOutput: createSuccessResult('OpenDialog created', {
          entityId: 'entity_123',
          title: 'Open'
        }),
        description: 'Create an entity picker dialog'
      }
    ],
    bad: []
  },
  
  handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
    const startTime = Date.now();
    const { title = 'Open', spaceId: providedSpaceId, windowId, createWindow = true, currentFolderId } = params;
    const spaceId = providedSpaceId || context.spaceId;
    
    try {
      // ✅ Capture previous active window for focus restoration
      const { useSpaceStore } = await import('../../stores/spaceStore');
      const previousActiveWindowId = useSpaceStore.getState().activeWindowId;
      
      log.log('[open_dialog_create] Creating OpenDialog entity:', { 
        title, 
        spaceId, 
        windowId, 
        currentFolderId,
        previousActiveWindowId 
      });
      
      // ✅ Use entityStore.actions for proper orchestration
      const useEntityStore = (await import('../../stores/entityStore')).default;
      const newEntity = await useEntityStore.getState().actions.createEntity({
        entityType: 'OpenDialog',
        name: title.trim(),
        spaceId: spaceId,
        skeleton: {
          '@type': 'OpenDialog',
          displayName: title.trim(),
          currentFolderId: currentFolderId || null,
          createdAt: new Date().toISOString(),
          lastAccessedAt: new Date().toISOString(),
          previousActiveWindowId  // Store for focus restoration on close
        }
      });
      
      log.log('[open_dialog_create] OpenDialog entity created:', { id: newEntity.id });
      
      const entityId = newEntity._id || newEntity.id;
      
      // Open the OpenDialog entity in a window
      if (createWindow) {
        log.log('[open_dialog_create] Opening OpenDialog entity in new window');
        const { getAppTypeForEntity } = await import('../../utils/entityTypeMapping');
        
        const appType = getAppTypeForEntity(newEntity);
        const createdWindowId = await useSpaceStore.getState().actions.createWindow({
          appType,
          entity: newEntity,
          position: { x: window.innerWidth / 2 - 400, y: window.innerHeight / 2 - 300 },
          size: { width: 800, height: 600 },
          applicationState: {
            openDialog: {
              currentFolderId: currentFolderId || null
            },
            previousActiveWindowId: previousActiveWindowId || undefined  // Convert null to undefined
          } as any  // TypeScript workaround for dynamic applicationState
        });
        
        // Bring to front
        useSpaceStore.getState().bringWindowToFront(createdWindowId);
      } else if (windowId) {
        log.log('[open_dialog_create] Opening OpenDialog entity in existing window:', windowId);
        
        // Update existing window to show OpenDialog entity
        await useSpaceStore.getState().actions.updateWindow(windowId, {
          entityId,
          entityLlmId: newEntity.llmId,
          appType: 'entity-view',
          title: 'Open',
          applicationState: {
            openDialog: {
              currentFolderId: currentFolderId || null
            },
            previousActiveWindowId: previousActiveWindowId || undefined  // Convert null to undefined
          } as any  // TypeScript workaround for dynamic applicationState
        });
      }
      
      return createSuccessResult(
        `OpenDialog created successfully`,
        {
          entityId: newEntity._id || newEntity.id,
          name: title.trim()
        },
        {
          format: 'json',
          metadata: {
            executionTime: Date.now() - startTime
          }
        }
      );
    } catch (error: any) {
      log.error('[open_dialog_create] Error:', error);
      return createErrorResult(
        'Failed to create OpenDialog',
        error.message || 'Unknown error',
        'execution'
      );
    }
  }
};

